/**
 * The root route's component and the page the shell owns itself.
 *
 * `Root` renders an outlet and nothing else. The chrome is deliberately not
 * here: the auth routes (`/sign-in`, `/sign-up`, `/reset-password`)
 * are reached by a caller who is signed out, and a navigation rendered from
 * capabilities they do not have yet -- inside a shell whose campaign switcher
 * is querying `/control/me` with no token -- is chrome around a form. The shell
 * is a pathless layout route below this one instead, so only the routes that
 * belong inside it get it.
 *
 * `NotFound` is re-exported from `../../chrome/not-found` instead of defined
 * here: the ops plane's root route needs the same page, and `src/ops` may
 * not import `src/` (`src/ops/routes/__tests__/composition.test.ts`).
 */

import { useEffect, type ReactNode } from 'react';
import { Activity, Building2, LogOut } from 'lucide-react';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';

import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { overviewPath } from '../data/overview/paths';
import { useAuthState, useSignOut } from '../session/authState';
import { lastPageOr } from '../session/lastPage';
import { readLastUsed } from '../session/lastUsed';
import { landingHref } from '../session/orgContext';
import { useMe } from '../session/session';
import { Forbidden, sessionGate } from '../shell/Forbidden';
import { ts } from '../shell/messages';
import { PageRoot } from '../tenant/onboarding/PageRoot';

export { NotFound } from '../../chrome/not-found';

export function Root(): ReactNode {
  return <Outlet />;
}

/** `/`. Resolves to the caller's last-used workspace, or their first
 *  reachable one, rather than rendering anything of its own. A campaign opens
 *  on its data, which is the plane's default surface (platform design §17.10);
 *  the switcher opens a listed campaign the same way (`campaignHref`).
 *  Mounted outside the shell (`../index.ts`'s `homeRoute`): a caller with no
 *  memberships has no organisation for the rail, the switcher or the account
 *  footer to point at, and every other branch renders nothing while it
 *  redirects, so no branch here needs the shell. `AppShell` no longer sits
 *  above this route to answer for Firebase still resolving or `/control/me`
 *  refusing, so `sessionGate` answers those here instead. */
export function Home(): ReactNode {
  const me = useMe();
  const signOut = useSignOut();
  const session = useAuthState();
  const uid = session.user?.uid ?? null;
  const navigate = useNavigate();

  const href =
    me.data !== undefined && me.data.memberships.length > 0
      ? landingHref(
          me.data.organisations,
          uid === null ? null : readLastUsed(uid),
          lastPageOr(overviewPath),
        )
      : null;

  // `<Navigate>`'s own re-fire guard compares the JSX props object by
  // reference, which is a fresh object on every render regardless of what
  // triggered it; keying an effect on the resolved string is what actually
  // navigates once per destination rather than once per render.
  useEffect(() => {
    if (href !== null) void navigate({ to: href, replace: true });
  }, [href, navigate]);

  const gate = sessionGate(session, me.error ?? null, () => void me.refetch());
  if (gate !== null) return gate;

  if (me.data === undefined) return null;

  if (me.data.memberships.length === 0) {
    return (
      <PageRoot className="max-w-md">
        <Card className="w-full">
          <CardContent className="flex flex-col items-center gap-5 text-center">
            <div className="flex items-center gap-2 self-start">
              <span className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
                <Activity aria-hidden className="size-4" />
              </span>
              {/* eslint-disable-next-line no-restricted-syntax -- the product's name, not copy: it does not change with locale */}
              <span className="text-base font-semibold text-foreground">SocialSrc</span>
            </div>

            <span className="flex size-12 items-center justify-center rounded-full bg-secondary">
              <Building2 aria-hidden className="size-5 text-muted-foreground" />
            </span>

            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {ts('home.noOrganisation.title')}
              </h1>
              <p className="text-sm text-muted-foreground">{ts('home.noOrganisation.body')}</p>
            </div>

            <Button asChild className="w-full">
              <Link to="/comecar">{ts('home.noOrganisation.createOrganisation')}</Link>
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => void signOut()}
            >
              <LogOut aria-hidden className="size-3.5" />
              {ts('home.noOrganisation.switchAccount')}
            </Button>
          </CardContent>
        </Card>
      </PageRoot>
    );
  }

  // Every listed organisation has a default (platform design §5.1); a
  // listing without one is refused rather than given a page of its own.
  return href === null ? <Forbidden /> : null;
}
